/**
 * The `@@cotera-tool-state` marker convention used across the app to flag a tool
 * result that is not yet final. The agent loop ("ATC") and
 * {@link ToolPart.isIncomplete} key off this marker to avoid advancing past a
 * step whose tool is still being processed.
 *
 * Mirrors the server-side `incomplete()` helper in
 * `apps/server/src/features/tools-v2/cotera-tool.ts` — kept here so SDK render
 * code (which cannot import from the server) can produce the same marker.
 */
export const TOOL_STATE_KEY = '@@cotera-tool-state';

export type IncompleteMarker = {
  '@@cotera-tool-state': 'incomplete';
};

/**
 * Wrap a tool output so it is treated as still-in-progress. The added marker
 * tells the agent loop not to advance past this tool call while it waits.
 */
export function incomplete<T extends Record<string, unknown>>(
  data: T
): T & IncompleteMarker {
  const marker: IncompleteMarker = { [TOOL_STATE_KEY]: 'incomplete' };
  return { ...data, ...marker };
}

/** True when a tool result carries the {@link incomplete} marker. */
export function isToolIncomplete(toolResult: unknown): boolean {
  return (
    toolResult !== null &&
    toolResult !== undefined &&
    typeof toolResult === 'object' &&
    !Array.isArray(toolResult) &&
    !(toolResult instanceof Date) &&
    (toolResult as Record<string, unknown>)[TOOL_STATE_KEY] === 'incomplete'
  );
}

/** Key on incomplete tool results that signals a post-execution conversation mutation. */
export const TOOL_PENDING_ACTION_KEY = '@@cotera-tool-pending-action' as const;

export enum ToolPendingAction {
  AskForCreds = 'ask-for-creds',
  AwaitApproval = 'await-approval',
}

export function getToolPendingAction(
  result: unknown
): ToolPendingAction | undefined {
  if (result === null || typeof result !== 'object' || Array.isArray(result)) {
    return undefined;
  }
  const action = (result as Record<string, unknown>)[TOOL_PENDING_ACTION_KEY];

  switch (action) {
    case ToolPendingAction.AskForCreds:
      return ToolPendingAction.AskForCreds;
    case ToolPendingAction.AwaitApproval:
      return ToolPendingAction.AwaitApproval;
    default:
      return undefined;
  }
}
